import { useCallback, useEffect, useState } from 'react';

export type ThemeMode = 'dark' | 'light';

const STORAGE_KEY = 'kory-theme';

function readStored(): ThemeMode {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'light' ? 'light' : 'dark';
  } catch {
    return 'dark';
  }
}

function apply(mode: ThemeMode) {
  // `:root` em styles.css já É o modo escuro — só "light" precisa do
  // atributo, mantendo o caso padrão (escuro) com o HTML mínimo.
  if (mode === 'light') document.documentElement.setAttribute('data-theme', 'light');
  else document.documentElement.removeAttribute('data-theme');
}

/**
 * Preferência de tema (Etapa 2 do plano de UX/UI — ver `~/Desktop/Kory
 * RevOps Hub - Plano UX-UI e Modo Claro.md`). Toggle manual, não segue o
 * SO (decisão confirmada com o Rodrigo) — persistido em `localStorage`,
 * aplicado via atributo `data-theme` no `<html>`, que os tokens de
 * `lib/theme.ts` (`C.*`/`ink()`) já leem via CSS var. O `index.html` tem
 * um script inline que aplica o valor salvo ANTES do React montar, pra não
 * piscar o tema errado no primeiro paint.
 */
export function useTheme() {
  const [mode, setModeState] = useState<ThemeMode>(() => readStored());

  useEffect(() => { apply(mode); }, [mode]);

  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch { /* localStorage indisponível, só não persiste */ }
  }, []);

  const toggle = useCallback(() => {
    setModeState((prev) => {
      const next = prev === 'light' ? 'dark' : 'light';
      try { localStorage.setItem(STORAGE_KEY, next); } catch { /* ignora */ }
      return next;
    });
  }, []);

  return { mode, setMode, toggle };
}
